import { useMemo, useRef, useState } from "react";
import { Header } from "@/components/Header";
import { StaticRouteBreadcrumbs } from "@/components/StaticRouteBreadcrumbs";
import { Footer } from "@/components/Footer";
import { AnnouncementBar } from "@/components/AnnouncementBar";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { ArrowRight, Clock, Sparkles } from "lucide-react";
import { ScrollReveal, ScrollRevealItem } from "@/components/ScrollReveal";
import { motion, useScroll, useTransform } from "framer-motion";
import { Button } from "@/components/ui/button";
import {
  CLUSTER_ORDER,
  JOURNAL_ARTICLES,
  getArticleBySlug,
  loadArticleBody,
  type JournalArticle,
} from "@/data/journalArticles";

import { routeHead } from "@/lib/routeHead";
import { ideasItemListSchema } from "../../scripts/page-schemas.mjs";

// Title/description come from scripts/routes.mjs, like the prerendered head.
const head = routeHead("/ideas");
// The guide list the prerendered page carries; its breadcrumb comes from
// StaticRouteJsonLd, like every flat route's.
const itemListJsonLd = JSON.stringify(ideasItemListSchema(JOURNAL_ARTICLES));

// Editorially chosen entry points — the guides that answer the most common
// pre-purchase questions.
const START_WITH = [
  "rug-size-guide",
  "sofa-dimensions-guide",
  "sectional-vs-sofa",
  "dining-table-size-guide",
  "mattress-size-chart",
];

const PAGE_SIZE = 24;


const ArticleCard = ({ article }: { article: JournalArticle }) => (
  <Link
    to={`/ideas/${article.slug}`}
    className="group block"
    onMouseEnter={() => void loadArticleBody(article.slug)}
    onFocus={() => void loadArticleBody(article.slug)}
  >
    <div className="aspect-[4/3] overflow-hidden mb-4 bg-muted">
      {article.hero ? (
        <img
          src={article.hero}
          alt={article.title}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
      ) : (
        <div className="w-full h-full bg-gradient-to-br from-muted to-muted-foreground/20" />
      )}
    </div>
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <span className="font-semibold text-primary tracking-wide uppercase text-[10px]">
          {article.cluster}
        </span>
        <span className="text-border">·</span>
        <Clock className="h-3 w-3" />
        <span>{article.readMinutes} min read</span>
      </div>
      <h3 className="font-display text-lg font-semibold text-foreground group-hover:text-primary transition-colors duration-300 leading-snug">
        {article.title}
      </h3>
      <p className="text-sm text-muted-foreground leading-relaxed line-clamp-2">
        {article.description}
      </p>
    </div>
  </Link>
);

const FeaturedArticle = ({ article }: { article: JournalArticle }) => (
  <ScrollReveal>
    <Link to={`/ideas/${article.slug}`} className="group block">
      <div className="grid md:grid-cols-2 gap-0 overflow-hidden border border-border/50">
        <div className="aspect-[4/3] md:aspect-auto md:min-h-[420px] overflow-hidden bg-muted">
          {article.hero && (
            <img
              src={article.hero}
              alt={article.title}
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.05]"
            />
          )}
        </div>
        <div className="flex flex-col justify-center p-6 md:p-10 lg:p-14 bg-card">
          <span className="text-[11px] tracking-[0.2em] uppercase text-primary font-semibold mb-3">
            {article.cluster}
          </span>
          <h2 className="font-display text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground group-hover:text-primary transition-colors duration-300 mb-4 leading-tight">
            {article.title}
          </h2>
          <p className="text-muted-foreground leading-relaxed mb-6 max-w-md">
            {article.description}
          </p>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Clock className="h-3.5 w-3.5" />
              <span>{article.readMinutes} min read</span>
            </div>
            <span className="text-[12px] tracking-widest uppercase text-primary font-medium flex items-center gap-2 group-hover:gap-3 transition-all duration-300">
              Read Article <ArrowRight className="h-3.5 w-3.5" />
            </span>
          </div>
        </div>
      </div>
    </Link>
  </ScrollReveal>
);

const Ideas = () => {
  const heroRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  // Only include clusters that have at least one published article.
  const clustersInUse = useMemo(
    () =>
      CLUSTER_ORDER.filter((c) =>
        JOURNAL_ARTICLES.some((a) => a.cluster === c),
      ),
    [],
  );

  const counts = useMemo(() => {
    const map: Record<string, number> = { All: JOURNAL_ARTICLES.length };
    JOURNAL_ARTICLES.forEach((a) => {
      map[a.cluster] = (map[a.cluster] || 0) + 1;
    });
    return map;
  }, []);

  const starters = useMemo(
    () => START_WITH.map((slug) => getArticleBySlug(slug)).filter(Boolean) as JournalArticle[],
    [],
  );

  const [activeCluster, setActiveCluster] = useState<string>("All");
  const [visible, setVisible] = useState(PAGE_SIZE);

  const filtered = useMemo(
    () =>
      activeCluster === "All"
        ? JOURNAL_ARTICLES
        : JOURNAL_ARTICLES.filter((a) => a.cluster === activeCluster),
    [activeCluster],
  );

  const featured = filtered[0];
  const rest = filtered.slice(1);
  const shown = rest.slice(0, visible);

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>{head.title}</title>
        <meta
          name="description"
          content={head.description}
        />
        <link rel="canonical" href="https://loftloom.com/ideas" />
        <meta property="og:title" content={head.title} />
        <meta
          property="og:description"
          content={head.description}
        />
        <meta property="og:url" content="https://loftloom.com/ideas" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{itemListJsonLd}</script>
      </Helmet>
      <AnnouncementBar />
      <Header />
      <StaticRouteBreadcrumbs />
      <main className="flex-1">
        {/* Cinematic Hero */}
        <div ref={heroRef} className="relative h-[60vh] md:h-[70vh] overflow-hidden">
          <motion.div style={{ y: heroY }} className="absolute inset-0">
            <video
              src="/video/ideas-hero.mp4"
              poster="/video/ideas-hero-poster.jpg"
              autoPlay
              muted
              loop
              playsInline
              className="w-full h-full object-cover scale-110"
            />
          </motion.div>
          <div className="absolute inset-0 bg-gradient-to-t from-foreground/70 via-foreground/30 to-foreground/10" />
          <motion.div
            style={{ opacity: heroOpacity }}
            className="absolute inset-0 flex flex-col items-center justify-center text-center px-4"
          >
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="text-[11px] md:text-[12px] tracking-[0.3em] uppercase text-primary-foreground/70 font-medium mb-4"
            >
              LoftLoom Journal
            </motion.span>
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.7 }}
              className="font-display text-4xl md:text-6xl lg:text-7xl font-semibold text-primary-foreground max-w-4xl leading-[1.1]"
            >
              Ideas &<br className="hidden md:block" /> Inspiration
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.6 }}
              className="text-primary-foreground/70 text-base md:text-lg mt-5 max-w-lg"
            >
              Sizing guides, honest comparisons, and styling ideas — to help you build a home that lasts.
            </motion.p>
          </motion.div>
        </div>

        {/* Cluster Filters */}
        <div className="border-b border-border/50 sticky top-[60px] z-30 bg-background/95 backdrop-blur-md">
          <div className="max-w-6xl mx-auto px-4">
            <div className="flex items-center gap-1 overflow-x-auto scrollbar-hide py-3 -mx-4 px-4 md:flex-wrap md:overflow-visible md:justify-center">
              {["All", ...clustersInUse].map((cat) => (
                <button
                  key={cat}
                  onClick={() => {
                    setActiveCluster(cat);
                    setVisible(PAGE_SIZE);
                  }}
                  className={`text-[11px] tracking-[0.15em] uppercase font-medium px-4 py-2 rounded-full whitespace-nowrap transition-all duration-300 ${
                    activeCluster === cat
                      ? "bg-foreground text-background"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                  }`}
                >
                  {cat}
                  {counts[cat] ? ` (${counts[cat]})` : ""}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Featured */}
        {featured && (
          <div className="max-w-6xl mx-auto px-4 pt-12 md:pt-16">
            <FeaturedArticle article={featured} />
          </div>
        )}

        {/* Start with these */}
        {activeCluster === "All" && starters.length > 0 && (
          <ScrollReveal>
            <div className="max-w-6xl mx-auto px-4 pt-12 md:pt-16">
              <h2 className="text-[11px] tracking-[0.22em] uppercase font-bold text-foreground mb-5">
                Start with these
              </h2>
              <div className="flex gap-4 overflow-x-auto scrollbar-hide -mx-4 px-4 md:mx-0 md:px-0 md:grid md:grid-cols-5">
                {starters.map((a) => (
                  <Link
                    key={a.slug}
                    to={`/ideas/${a.slug}`}
                    className="group block shrink-0 w-[62%] sm:w-[40%] md:w-auto"
                  >
                    <div className="aspect-[4/3] overflow-hidden mb-3 bg-muted">
                      {a.hero && (
                        <img
                          src={a.hero}
                          alt={a.title}
                          loading="lazy"
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        />
                      )}
                    </div>
                    <h3 className="font-display text-sm font-semibold leading-snug text-foreground group-hover:text-primary transition-colors line-clamp-3">
                      {a.title}
                    </h3>
                  </Link>
                ))}
              </div>
            </div>
          </ScrollReveal>
        )}

        {/* Editorial Quote Strip */}
        <ScrollReveal>
          <div className="bg-primary/5 border-y border-primary/10 my-12 md:my-16">
            <div className="max-w-4xl mx-auto px-4 py-10 md:py-14 text-center">
              <Sparkles className="h-5 w-5 text-primary mx-auto mb-4" />
              <blockquote className="font-display text-xl md:text-2xl lg:text-3xl text-foreground leading-relaxed italic">
                "Great design isn't about following rules — it's about understanding them well enough to know when to break them."
              </blockquote>
              <p className="text-sm text-muted-foreground mt-4 tracking-wide uppercase">
                — LoftLoom
              </p>
            </div>
          </div>
        </ScrollReveal>

        {/* Grid */}
        <div className="max-w-6xl mx-auto px-4 pb-16 md:pb-24">
          {shown.length > 0 && (
            <ScrollReveal
              stagger
              staggerDelay={0.05}
              className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-10"
            >
              {shown.map((article) => (
                <ScrollRevealItem key={article.slug}>
                  <ArticleCard article={article} />
                </ScrollRevealItem>
              ))}
            </ScrollReveal>
          )}

          {visible < rest.length && (
            <div className="mt-12 text-center">
              <p className="text-xs text-muted-foreground mb-4 tracking-wide">
                Showing {shown.length + 1} of {filtered.length} articles
              </p>
              <Button
                variant="outline"
                onClick={() => setVisible((v) => v + PAGE_SIZE)}
                className="rounded-none text-[12px] tracking-widest uppercase px-8 py-5"
              >
                Load more articles
              </Button>
            </div>
          )}

          <ScrollReveal>
            <div className="mt-16 md:mt-20 border border-border/50 p-8 md:p-12 text-center bg-card">
              <h3 className="font-display text-2xl md:text-3xl font-semibold text-foreground mb-3">
                Get Design Tips in Your Inbox
              </h3>
              <p className="text-muted-foreground max-w-md mx-auto mb-6">
                Join the newsletter for new guides, room ideas and new arrivals.
              </p>
              <Button
                asChild
                className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-none text-[12px] tracking-widest uppercase px-8 py-5"
              >
                <Link to="/#newsletter">
                  Subscribe Now <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </ScrollReveal>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Ideas;
